import { Button } from '@bambi/component-library/src/components/Button';
import { DateTime } from 'luxon';

import { MembershipRead } from 'src/common/external/bambi-api/bambiApi';
import { Avatar } from 'src/common/primitives/Avatar';
import { Modal, ModalProps } from 'src/common/primitives/Modal';
import { ModalTitle } from 'src/common/primitives/Modal/ModalTitle';
import { Well } from 'src/common/primitives/Well/Well';
import { formatFullName } from 'src/common/util/formatFullName';

interface MemberRestorePromptModalProps
  extends Omit<ModalProps, 'title' | 'children'> {
  membership: MembershipRead | null;
  onRestoreClick: (membership: MembershipRead) => void;
}

export function MemberRestorePromptModal({
  open,
  membership,
  setOpen,
  onClose,
  onRestoreClick,
}: MemberRestorePromptModalProps) {
  if (!membership) {
    return null;
  }

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      onClose={onClose}
      title="Duplicate membership detected, restore?"
      contentClassnames="max-w-md"
    >
      <div className="flex flex-col gap-2">
        <ModalTitle>Restore an existing account?</ModalTitle>
        <p>
          An account for <strong>{membership.email}</strong> already exists, but
          was previously deleted.
        </p>
        <Well skipPrintable>
          <div className="flex flex-row gap-2">
            <Avatar
              firstName={membership.first_name}
              lastName={membership.last_name}
            />
            <div className="flex flex-col">
              <strong>{formatFullName(membership, () => null)}</strong>
              <span
                className="text-sm text-gray-500"
                data-testid="deleted-date"
              >
                Deleted on{' '}
                {DateTime.fromISO(membership.deleted_at!).toLocaleString(
                  DateTime.DATETIME_SHORT
                )}
              </span>
            </div>
          </div>
        </Well>
        <div className="flex flex-row gap-2">
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="w-full"
            onClick={() => onRestoreClick(membership)}
          >
            Restore
          </Button>
        </div>
      </div>
    </Modal>
  );
}
